import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPHP, formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { Plus, Search, Download, Filter } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Employees() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        first_name: "", middle_name: "", last_name: "", email: "", position: "",
        employment_type: "regular", hire_date: new Date().toISOString().slice(0, 10),
        basic_monthly: 25000, department_id: "", branch_id: "", benefits_entitled: true,
        nationality: "Filipino", work_location: "", payment_method: "", payroll_frequency: "",
        salary_type: "monthly", rate: 25000,
        ec_name: "", ec_relationship: "", ec_phone: "",
    });
    const [departments, setDepartments] = useState([]);
    const [branches, setBranches] = useState([]);

    const load = async () => {
        const params = {};
        if (search) params.search = search;
        if (status) params.status = status;
        const { data } = await api.get("/hris/employees", { params });
        setItems(data.items || []);
        setTotal(data.total || 0);
    };
    useEffect(() => { load(); }, [search, status]);
    useEffect(() => {
        api.get("/hris/departments").then((r) => setDepartments(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
        api.get("/hris/branches").then((r) => setBranches(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
    }, []);

    const create = async () => {
        setCreating(true);
        try {
            const payload = {
                first_name: form.first_name, middle_name: form.middle_name || null, last_name: form.last_name, email: form.email,
                position: form.position, employment_type: form.employment_type, hire_date: form.hire_date,
                employment_status: "active",
                department_id: form.department_id || null, branch_id: form.branch_id || null,
                benefits_entitled: ["regular", "probationary"].includes(form.employment_type) ? true : !!form.benefits_entitled,
                nationality: form.nationality || null, work_location: form.work_location || null,
                payment_method: form.payment_method || null, payroll_frequency: form.payroll_frequency || null,
                emergency_contact: { name: form.ec_name || null, relationship: form.ec_relationship || null, phone: form.ec_phone || null },
                salary: form.salary_type === "monthly"
                    ? { salary_type: "monthly", basic_monthly: Number(form.basic_monthly), daily_rate: Number(form.basic_monthly) / 22, hourly_rate: Number(form.basic_monthly) / 22 / 8, allowances: [] }
                    // the server works out the other two rates from the one typed here
                    : { salary_type: form.salary_type, [form.salary_type === "daily" ? "daily_rate" : "hourly_rate"]: Number(form.rate), allowances: [] },
            };
            await api.post("/hris/employees", payload);
            toast.success("Employee created");
            setOpen(false);
            setForm({ ...form, first_name: "", last_name: "", email: "", position: "" });
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
        } finally { setCreating(false); }
    };

    return (
        <div>
            <PageHeader
                title="Employees"
                subtitle={`${total} record${total === 1 ? "" : "s"} in active company`}
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 flex items-center gap-2" data-testid="add-employee-btn">
                                <Plus className="size-4" /> Add Employee
                            </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
                            <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>First Name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" /></div>
                                <div><Label>Middle Name</Label><Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} data-testid="emp-middle-name" /></div>
                                <div><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last-name" /></div>
                                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" /></div>
                                <div><Label>Position</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="emp-position" /></div>
                                <div>
                                    <Label>Employment Type</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} data-testid="emp-type">
                                        <option value="regular">Regular</option>
                                        <option value="probationary">Probationary</option>
                                        <option value="contractor">Contractual</option>
                                        <option value="project">Project-based</option>
                                        <option value="agency">Agency-based</option>
                                        <option value="daily">Daily</option>
                                    </select>
                                </div>
                                {!["regular", "probationary"].includes(form.employment_type) && (
                                    <div className="col-span-2 border border-border rounded-md p-2.5">
                                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                                            <input type="checkbox" checked={!!form.benefits_entitled} onChange={(e) => setForm({ ...form, benefits_entitled: e.target.checked })} data-testid="emp-benefits" />
                                            <span className="font-medium">Entitled to government benefits (SSS / PhilHealth / Pag-IBIG)</span>
                                        </label>
                                        <div className="text-[11px] text-muted-foreground mt-1 ml-6">
                                            {form.benefits_entitled
                                                ? "Contributions will be deducted and remitted — they appear on the payslip."
                                                : "No statutory contributions will be computed for this employee — the payslip shows ₱0 for SSS/PhilHealth/Pag-IBIG."}
                                        </div>
                                    </div>
                                )}
                                <div><Label>Hire Date</Label><Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="emp-hire-date" /></div>
                                <div>
                                    <Label>Salary Type</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.salary_type} onChange={(e) => setForm({ ...form, salary_type: e.target.value })} data-testid="emp-salary-type">
                                        <option value="monthly">Monthly</option>
                                        <option value="daily">Daily</option>
                                        <option value="hourly">Hourly</option>
                                    </select>
                                </div>
                                {form.salary_type === "monthly"
                                    ? <div><Label>Basic Monthly (PHP)</Label><Input type="number" value={form.basic_monthly} onChange={(e) => setForm({ ...form, basic_monthly: e.target.value })} data-testid="emp-basic" /></div>
                                    : <div><Label>{form.salary_type === "daily" ? "Daily Rate (PHP)" : "Hourly Rate (PHP)"}</Label><Input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="emp-rate" /></div>}
                                <div><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="emp-nationality" /></div>
                                <div><Label>Work Location</Label><Input value={form.work_location} onChange={(e) => setForm({ ...form, work_location: e.target.value })} data-testid="emp-work-location" /></div>
                                <div>
                                    <Label>Payroll Frequency</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.payroll_frequency} onChange={(e) => setForm({ ...form, payroll_frequency: e.target.value })} data-testid="emp-payroll-frequency">
                                        <option value="">Every run of the company</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="semi_monthly">Semi-monthly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Preferred Payment Method</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="emp-payment-method">
                                        <option value="">Bank transfer (default)</option>
                                        <option value="cash">Cash</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="e_wallet">E-wallet</option>
                                    </select>
                                </div>
                                <div className="col-span-2 text-xs font-semibold uppercase text-muted-foreground">Emergency contact</div>
                                <div><Label>Name</Label><Input value={form.ec_name} onChange={(e) => setForm({ ...form, ec_name: e.target.value })} data-testid="emp-ec-name" /></div>
                                <div><Label>Relationship</Label><Input value={form.ec_relationship} onChange={(e) => setForm({ ...form, ec_relationship: e.target.value })} data-testid="emp-ec-relationship" /></div>
                                <div><Label>Phone</Label><Input value={form.ec_phone} onChange={(e) => setForm({ ...form, ec_phone: e.target.value })} data-testid="emp-ec-phone" /></div>
                                <div>
                                    <Label>Department</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} data-testid="emp-department">
                                        <option value="">— No department —</option>
                                        {departments.map((d) => <option key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</option>)}
                                    </select>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">Add departments in Organization → + Department.</div>
                                </div>
                                <div>
                                    <Label>Branch</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="emp-branch">
                                        <option value="">— Head office / none —</option>
                                        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.region ? ` · ${b.region}` : ""}</option>)}
                                    </select>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">Branch region drives the minimum-wage floor.</div>
                                </div>
                            </div>
                            <DialogFooter>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium" onClick={create} disabled={creating} data-testid="emp-create-submit">{creating ? "Creating…" : "Create Employee"}</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            <Surface className="p-3 mb-3 flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        placeholder="Search name, employee no, email…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-9 pl-9 pr-3 rounded-md border border-border text-sm"
                        data-testid="emp-search"
                    />
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-3 rounded-md border border-border text-sm" data-testid="emp-status-filter">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="on_leave">On leave</option>
                    <option value="resigned">Resigned</option>
                    <option value="terminated">Terminated</option>
                </select>
            </Surface>

            <TableWrap>
                <thead>
                    <tr>
                        <th>Emp No.</th>
                        <th>Name</th>
                        <th>Position</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Hire Date</th>
                        <th className="num">Basic (PHP)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((e) => (
                        <tr key={e.id} className="cursor-pointer" onClick={() => navigate(`/hris/employees/${e.id}`)} data-testid={`emp-row-${e.employee_no}`}>
                            <td className="font-mono">{e.employee_no}</td>
                            <td className="font-medium">{e.last_name}, {e.first_name}</td>
                            <td>{e.position || "—"}</td>
                            <td><StatusBadge value={e.employment_type} /></td>
                            <td><StatusBadge value={e.employment_status} /></td>
                            <td>{formatDate(e.hire_date)}</td>
                            <td className="num">{formatPHP(e.salary?.basic_monthly || 0)}</td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={7}><Empty title="No employees found" subtitle="Adjust filters or add a new employee." /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}
